import { defineRoute } from "src/routing/defineRoute";
// Avoid `src/utils` barrel: it cycles back through routesDef → this file during module init.
import { stageCodeToNameMapper } from "src/utils/mappers";

/**
 * Path + typed URL builder pairs for migrated `/projects` URLs.
 *
 * Kept separate from `projects.ts` (the `RouteObject` tree) so `RouteUrls.ts` and pages can
 * import paths without creating a cycle through page `Component` imports.
 *
 * Full absolute strings (RR6 requires a child's path to start with its parent's).
 * `/developments` paths live in `developmentsPaths.ts`.
 */
export const projectsRoute = defineRoute("/projects");

export const projectRoute = defineRoute(`${projectsRoute.path}/:projectId`);

export const projectDashboardRoute = defineRoute(`${projectRoute.path}/dashboard`);
export const projectTeamRoute = defineRoute(`${projectRoute.path}/team`);
export const projectSettingsRoute = defineRoute(`${projectRoute.path}/settings`);
export const projectItemsRoute = defineRoute(`${projectRoute.path}/items`);
export const projectFeatureFlagTestRoute = defineRoute(`${projectRoute.path}/feature-flag-test`);
export const projectBillRoute = defineRoute(`${projectRoute.path}/bills/:billId`);
/** RR6 matching path — optional `:billId` (typed builder uses `projectBillRoute`). */
export const projectBillOptionalPath = `${projectRoute.path}/bills/:billId?`;

export const projectBudgetBaseRoute = defineRoute(`${projectRoute.path}/budget`);
export const projectBudgetRoute = defineRoute(`${projectRoute.path}/budget/:stageSlug`);
export const projectBudgetMetricsRoute = defineRoute(`${projectRoute.path}/budget-metrics`);
export const projectBudgetReallocationRoute = defineRoute(
  `${projectRoute.path}/budget-reallocations/:stageSlug/:idOrAdd`,
);

export const projectScheduleBaseRoute = defineRoute(`${projectRoute.path}/schedule`);
export const projectScheduleRoute = defineRoute(`${projectRoute.path}/schedule/:stageSlug`);
export const projectDynamicSchedulesRoute = defineRoute(`${projectRoute.path}/dynamicSchedule`);

export const projectContractsRoute = defineRoute(`${projectRoute.path}/homeowner-contracts`);
export const projectContractRoute = defineRoute(`${projectRoute.path}/homeowner-contracts/:contractId`);
export const projectContractChangeOrderRoute = defineRoute(
  `${projectRoute.path}/homeowner-contracts/change-order/:changeOrderId`,
);
/** RR6 matching path — optional `:changeOrderId`. */
export const projectContractChangeOrderOptionalPath = `${projectRoute.path}/homeowner-contracts/change-order/:changeOrderId?`;

export const projectCommitmentsRoute = defineRoute(`${projectRoute.path}/commitments`);
export const projectCommitmentRoute = defineRoute(`${projectRoute.path}/commitments/:idOrAdd`);
export const projectCommitmentChangeOrderRoute = defineRoute(
  `${projectRoute.path}/commitments/:commitmentId/change-order/:idOrAdd`,
);
export const projectReleaseToTradesRoute = defineRoute(`${projectRoute.path}/release-to-trades`);

export const projectDropsRoute = defineRoute(`${projectRoute.path}/drops`);
export const projectHomeownerNotesRoute = defineRoute(`${projectRoute.path}/homeowner-notes`);

export const projectInvoicesRoute = defineRoute(`${projectRoute.path}/invoices`);
export const projectInvoiceRoute = defineRoute(`${projectRoute.path}/invoices/:idOrAdd`);

export const projectEstimatesRoute = defineRoute(`${projectRoute.path}/estimates`);
export const projectEstimateRoute = defineRoute(`${projectRoute.path}/estimates/:estimateId`);

export const projectExpensesRoute = defineRoute(`${projectRoute.path}/expenses`);
export const projectFinishScheduleRoute = defineRoute(`${projectRoute.path}/finish-schedule`);

export const projectSelectionsBaseRoute = defineRoute(`${projectRoute.path}/selections`);
export const projectSelectionsPreConRoute = defineRoute(
  `${projectRoute.path}/selections/${stageCodeToNameMapper.PRE_CON_EXECUTION}`,
);
export const projectSelectionsPreConDetailsRoute = defineRoute(
  `${projectRoute.path}/selections/${stageCodeToNameMapper.PRE_CON_EXECUTION}/:projectItemId`,
);
export const projectSelectionsConstructionRoute = defineRoute(
  `${projectRoute.path}/selections/${stageCodeToNameMapper.CONSTRUCTION}`,
);
export const projectSelectionsConstructionDetailsRoute = defineRoute(
  `${projectRoute.path}/selections/${stageCodeToNameMapper.CONSTRUCTION}/:projectItemId`,
);

export const projectPreConServicesRoute = defineRoute(`${projectRoute.path}/pre-con-services`);
export const projectPreConServicesDetailRoute = defineRoute(`${projectRoute.path}/pre-con-services/:projectItemId`);

export const projectLotSummaryRoute = defineRoute(`${projectRoute.path}/lot-summary`);

export const projectSpecsAndSelectionsRoute = defineRoute(`${projectRoute.path}/specs-and-selections`);
export const projectSpecsAndSelectionsDetailRoute = defineRoute(
  `${projectRoute.path}/specs-and-selections/:projectItemId`,
);

export const projectToDosRoute = defineRoute(`${projectRoute.path}/to-dos`);
export const projectToDoModalRoute = defineRoute(`${projectRoute.path}/to-dos/:toDoId`);

export const projectJobLogsRoute = defineRoute(`${projectRoute.path}/job-logs`);
export const projectJobLogsGalleryRoute = defineRoute(`${projectRoute.path}/job-logs/gallery`);
export const projectJobLogNoteDrawerRoute = defineRoute(`${projectRoute.path}/job-logs/:jobLogId/:jobLogNoteId`);

export const projectDocumentsRoute = defineRoute(`${projectRoute.path}/documents`);

export const projectChangeEventsRoute = defineRoute(`${projectRoute.path}/change-events`);
export const projectGuidedChangeEventRoute = defineRoute(`${projectRoute.path}/change-events/guided/:idOrAdd`);
export const projectChangeEventRoute = defineRoute(`${projectRoute.path}/change-events/:changeEventId`);
export const projectChangeEventLineItemsRoute = defineRoute(
  `${projectRoute.path}/change-events/:changeEventId/line-items`,
);
export const projectChangeEventOverviewRoute = defineRoute(
  `${projectRoute.path}/change-events/:changeEventId/overview`,
);
export const projectChangeEventEstimatesRoute = defineRoute(
  `${projectRoute.path}/change-events/:changeEventId/estimates`,
);
export const projectChangeEventCommitmentsRoute = defineRoute(
  `${projectRoute.path}/change-events/:changeEventId/commitments`,
);
export const projectChangeEventHistoryRoute = defineRoute(`${projectRoute.path}/change-events/:changeEventId/history`);
export const projectChangeEventRposRoute = defineRoute(`${projectRoute.path}/change-events/:changeEventId/rpos`);
export const projectChangeEventLineItemRoute = defineRoute(
  `${projectRoute.path}/change-events/:changeEventId/line-items/:celiId`,
);
export const projectChangeEventLineItemHistoryRoute = defineRoute(
  `${projectRoute.path}/change-events/:changeEventId/line-items/:celiId/history`,
);
export const projectChangeEventLineItemSelectionRoute = defineRoute(
  `${projectRoute.path}/change-events/:changeEventId/line-items/:celiId/selection`,
);
export const projectChangeLogRoute = defineRoute(`${projectRoute.path}/change-log`);
export const projectChangeLogDetailsRoute = defineRoute(`${projectRoute.path}/change-log/:changeRequestId`);

export const projectProductOfferingsRoute = defineRoute(`${projectRoute.path}/product-offerings`);
export const projectProductOfferingRoute = defineRoute(
  `${projectRoute.path}/product-offerings/:productOfferingId/:rpavId`,
);
/** RR6 matching path — optional `:rpavId`. */
export const projectProductOfferingOptionalPath = `${projectRoute.path}/product-offerings/:productOfferingId/:rpavId?`;
export const projectProductOfferingApplyToProjectRoute = defineRoute(
  `${projectRoute.path}/product-offerings/:productOfferingId/:rpavId/apply`,
);
export const projectProductOfferingVersionHistoryRoute = defineRoute(
  `${projectRoute.path}/product-offerings/:productOfferingId/history`,
);
export const projectProductOfferingSpecificConfigsRoute = defineRoute(
  `${projectRoute.path}/product-offerings/:productOfferingId/product-offering-configs`,
);
export const projectProductOfferingConfigsRoute = defineRoute(`${projectRoute.path}/product-offering-configs`);
export const projectProductOfferingSpecificConfigsCompareRoute = defineRoute(
  `${projectRoute.path}/product-offering-configs/:productOfferingId/compare`,
);
export const projectProductOfferingConfigsCompareRoute = defineRoute(
  `${projectRoute.path}/product-offering-configs/compare`,
);
export const projectProductOfferingConfigAppliableRoute = defineRoute(
  `${projectRoute.path}/appliable-product-offering-configs`,
);
export const projectProductOfferingConfigApplyRoute = defineRoute(
  `${projectRoute.path}/apply-product-offering-config/:configId`,
);

export const projectBidPackagesRoute = defineRoute(`${projectRoute.path}/bid-packages`);
export const projectBidCoverageRoute = defineRoute(`${projectRoute.path}/bid-coverage`);

export const projectBidContractsRoute = defineRoute(`${projectRoute.path}/bid-contracts`);
export const projectBidContractRoute = defineRoute(`${projectRoute.path}/bid-contracts/:bidContractRevisionId`);
export const projectBidContractOverviewRoute = defineRoute(
  `${projectRoute.path}/bid-contracts/:bidContractRevisionId/overview`,
);
export const projectBidContractPlanPricingRoute = defineRoute(
  `${projectRoute.path}/bid-contracts/:bidContractRevisionId/plan-pricing`,
);
export const projectBidContractUnitPricingRoute = defineRoute(
  `${projectRoute.path}/bid-contracts/:bidContractRevisionId/unit-pricing`,
);
export const projectBidContractPurchaseOrdersRoute = defineRoute(
  `${projectRoute.path}/bid-contracts/:bidContractRevisionId/purchase-orders`,
);

export const projectDraftScheduleRoute = defineRoute(`${projectDynamicSchedulesRoute.path}/draft`);
export const projectDraftScheduleNewTaskRoute = defineRoute(`${projectDraftScheduleRoute.path}/new`);
export const projectDraftSchedulePublishRoute = defineRoute(`${projectDraftScheduleRoute.path}/publish`);
export const projectDraftScheduleCalendarRoute = defineRoute(`${projectDraftScheduleRoute.path}/calendar`);
export const projectDraftScheduleGanttRoute = defineRoute(`${projectDraftScheduleRoute.path}/gantt`);
export const projectDraftScheduleLookaheadRoute = defineRoute(`${projectDraftScheduleRoute.path}/lookahead`);
export const projectDraftScheduleMilestoneRoute = defineRoute(`${projectDraftScheduleRoute.path}/milestones`);
export const projectDynamicScheduleTaskRoute = defineRoute(`${projectDynamicSchedulesRoute.path}/:planTaskId`);
export const projectDynamicScheduleTaskMaterialsRoute = defineRoute(
  `${projectDynamicScheduleTaskRoute.path}/materials`,
);
export const projectDynamicScheduleMilestoneRoute = defineRoute(
  `${projectDynamicSchedulesRoute.path}/milestones/:planMilestoneId`,
);

/**
 * Paths surfaced in global search / path suggestions. Excludes rarely-navigated helpers like items.
 */
export const suggestableProjectDetailPaths: string[] = [
  projectRoute.path,
  projectDashboardRoute.path,
  projectTeamRoute.path,
  projectSettingsRoute.path,
  projectBillOptionalPath,
  projectBudgetBaseRoute.path,
  projectBudgetRoute.path,
  projectBudgetMetricsRoute.path,
  projectBudgetReallocationRoute.path,
  projectScheduleBaseRoute.path,
  projectScheduleRoute.path,
  projectDynamicSchedulesRoute.path,
  projectContractsRoute.path,
  projectContractRoute.path,
  projectContractChangeOrderOptionalPath,
  projectCommitmentsRoute.path,
  projectCommitmentRoute.path,
  projectCommitmentChangeOrderRoute.path,
  projectReleaseToTradesRoute.path,
  projectDropsRoute.path,
  projectHomeownerNotesRoute.path,
  projectInvoicesRoute.path,
  projectInvoiceRoute.path,
  projectEstimatesRoute.path,
  projectEstimateRoute.path,
  projectExpensesRoute.path,
  projectFinishScheduleRoute.path,
  projectSelectionsBaseRoute.path,
  projectSelectionsPreConRoute.path,
  projectSelectionsPreConDetailsRoute.path,
  projectSelectionsConstructionRoute.path,
  projectSelectionsConstructionDetailsRoute.path,
  projectPreConServicesRoute.path,
  projectPreConServicesDetailRoute.path,
  projectLotSummaryRoute.path,
  projectSpecsAndSelectionsRoute.path,
  projectSpecsAndSelectionsDetailRoute.path,
  projectToDosRoute.path,
  projectToDoModalRoute.path,
  projectJobLogsRoute.path,
  projectJobLogsGalleryRoute.path,
  projectJobLogNoteDrawerRoute.path,
  projectDocumentsRoute.path,
  projectChangeEventsRoute.path,
  projectChangeEventRoute.path,
  projectChangeEventLineItemsRoute.path,
  projectChangeEventOverviewRoute.path,
  projectChangeEventEstimatesRoute.path,
  projectChangeEventCommitmentsRoute.path,
  projectChangeEventHistoryRoute.path,
  projectChangeEventRposRoute.path,
  projectChangeEventLineItemRoute.path,
  projectChangeEventLineItemHistoryRoute.path,
  projectChangeEventLineItemSelectionRoute.path,
  projectChangeLogRoute.path,
  projectChangeLogDetailsRoute.path,
  projectProductOfferingsRoute.path,
  projectProductOfferingOptionalPath,
  projectProductOfferingApplyToProjectRoute.path,
  projectProductOfferingVersionHistoryRoute.path,
  projectProductOfferingSpecificConfigsRoute.path,
  projectProductOfferingConfigsRoute.path,
  projectProductOfferingSpecificConfigsCompareRoute.path,
  projectProductOfferingConfigsCompareRoute.path,
  projectProductOfferingConfigAppliableRoute.path,
  projectProductOfferingConfigApplyRoute.path,
  projectBidPackagesRoute.path,
  projectBidCoverageRoute.path,
  projectBidContractsRoute.path,
  projectBidContractRoute.path,
  projectBidContractOverviewRoute.path,
  projectBidContractPlanPricingRoute.path,
  projectBidContractUnitPricingRoute.path,
  projectBidContractPurchaseOrdersRoute.path,
];
